import { BusinessDetails, LegalLinks } from "components";
import { useBrand } from "hooks/useBrand";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Paths from "routes/Paths";

import FaqItem from "./FaqItem";
import SupportForm from "./SupportForm";

import styles from "./HelpSupportPage.module.css";

/** FAQ entries in display order; question and answer live under `faq.<key>` in the namespace. */
const FAQ_KEYS = [
	"whatIsTreeTier",
	"isItFree",
	"whoCanSee",
	"sharing",
	"stars",
	"layouts",
	"photos",
	"signIn",
	"billing",
	"reportProblem"
] as const;

const HelpSupportPage = () => {
	const { t } = useTranslation(["helpSupportPage", "common"]);
	const { brand } = useBrand();

	return (
		<main className={styles.page}>
			<header className={styles.header}>
				<Link aria-label={t("common:brandHome", { brand })} className={styles.brand} to={Paths.root}>
					<img alt="" className={styles.brandMark} src="/assets/landing/main-logo.png" />
					<span>{brand}</span>
				</Link>
				<Link className={styles.homeLink} to={Paths.root}>
					{t("common:backToHome")}
				</Link>
			</header>

			<section className={styles.hero}>
				<div className={styles.heroInner}>
					<p className={styles.eyebrow}>{t("helpSupportPage:eyebrow", { brand })}</p>
					<h1>{t("helpSupportPage:title")}</h1>
					<div aria-hidden="true" className={styles.flourish} />
					<p className={styles.heroSubtitle}>{t("helpSupportPage:subtitle")}</p>
				</div>
			</section>

			<div className={styles.body}>
				<section aria-labelledby="faq-heading" className={styles.faqCard}>
					<h2 className={styles.cardHeading} id="faq-heading">
						{t("helpSupportPage:faq.heading")}
					</h2>
					<p className={styles.cardSubheading}>{t("helpSupportPage:faq.description")}</p>
					<div className={styles.faqList}>
						{FAQ_KEYS.map((entryKey: string) => (
							<FaqItem key={entryKey} entryKey={entryKey} />
						))}
					</div>
				</section>

				<section aria-labelledby="contact-heading" className={styles.contactCard}>
					<h2 className={styles.cardHeading} id="contact-heading">
						{t("helpSupportPage:contact.heading")}
					</h2>
					<p className={styles.cardSubheading}>{t("helpSupportPage:contact.description")}</p>
					<SupportForm />

					<div className={styles.otherWays}>
						<h3 className={styles.otherWaysHeading}>{t("helpSupportPage:contact.otherWaysHeading")}</h3>
						<BusinessDetails className={styles.businessDetails} />
						<Link className={styles.contactPageLink} to={Paths.contact}>
							{t("helpSupportPage:contact.contactPageLink")}
						</Link>
					</div>
				</section>
			</div>

			<footer className={styles.footer}>
				<span>{t("common:copyright")}</span>
				<nav aria-label={t("common:sitePagesLabel")} className={styles.footerNav}>
					<LegalLinks />
				</nav>
			</footer>
		</main>
	);
};

export default HelpSupportPage;
